import { TFunction } from "i18next";
import {
  KeywordFeedbackCategory,
  KeywordFeedbackSubCategory,
} from "reedy-data/api";

export type IconState = "flag" | "unflag" | "view";

export const CATEGORIES: KeywordFeedbackCategory[] = [
  KeywordFeedbackCategory.WRONG_ASSOCIATION,
  KeywordFeedbackCategory.POOR_FIT,
  KeywordFeedbackCategory.DUPLICATE_REDUNDANT,
  KeywordFeedbackCategory.OTHER,
];

// Mirrors KEYWORD_FEEDBACK_VALID_SUB_CATEGORIES in
// functions/python/app/models/keyword_feedback.py — BE rejects invalid
// pairings, this map drives the pill UI.
export const SUB_CATEGORIES: Record<
  KeywordFeedbackCategory,
  KeywordFeedbackSubCategory[]
> = {
  [KeywordFeedbackCategory.WRONG_ASSOCIATION]: [
    KeywordFeedbackSubCategory.INAPPROPRIATE,
    KeywordFeedbackSubCategory.UNRELATED_REFERENCE,
  ],
  [KeywordFeedbackCategory.POOR_FIT]: [
    KeywordFeedbackSubCategory.IRRELEVANT,
    KeywordFeedbackSubCategory.TOO_SEASONAL,
    KeywordFeedbackSubCategory.TOO_GENERAL,
  ],
  [KeywordFeedbackCategory.DUPLICATE_REDUNDANT]: [
    KeywordFeedbackSubCategory.REPEAT,
    KeywordFeedbackSubCategory.METADATA_REFERENCE,
  ],
  [KeywordFeedbackCategory.OTHER]: [],
};

export const CATEGORY_TESTID: Record<KeywordFeedbackCategory, string> = {
  [KeywordFeedbackCategory.WRONG_ASSOCIATION]: "wrongAssociation",
  [KeywordFeedbackCategory.POOR_FIT]: "poorFit",
  [KeywordFeedbackCategory.DUPLICATE_REDUNDANT]: "duplicate",
  [KeywordFeedbackCategory.OTHER]: "other",
};

export const SUB_CATEGORY_TESTID: Record<KeywordFeedbackSubCategory, string> = {
  [KeywordFeedbackSubCategory.INAPPROPRIATE]: "inappropriate",
  [KeywordFeedbackSubCategory.UNRELATED_REFERENCE]: "unrelatedAuthorBookTv",
  [KeywordFeedbackSubCategory.IRRELEVANT]: "irrelevant",
  [KeywordFeedbackSubCategory.TOO_SEASONAL]: "tooSeasonal",
  [KeywordFeedbackSubCategory.TOO_GENERAL]: "tooGeneral",
  [KeywordFeedbackSubCategory.REPEAT]: "repeat",
  [KeywordFeedbackSubCategory.METADATA_REFERENCE]: "referencesBookMetadata",
  [KeywordFeedbackSubCategory.OTHER]: "other",
};

export const getCategoryLabel = (
  t: TFunction,
  category: KeywordFeedbackCategory,
): string => {
  switch (category) {
    case KeywordFeedbackCategory.WRONG_ASSOCIATION:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.categories.wrongAssociation",
      );
    case KeywordFeedbackCategory.POOR_FIT:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.categories.poorFit",
      );
    case KeywordFeedbackCategory.DUPLICATE_REDUNDANT:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.categories.duplicate",
      );
    default:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.categories.other",
      );
  }
};

export const getCategoryTooltip = (
  t: TFunction,
  category: KeywordFeedbackCategory,
): string => {
  switch (category) {
    case KeywordFeedbackCategory.WRONG_ASSOCIATION:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.categoryTooltips.wrongAssociation",
      );
    case KeywordFeedbackCategory.POOR_FIT:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.categoryTooltips.poorFit",
      );
    case KeywordFeedbackCategory.DUPLICATE_REDUNDANT:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.categoryTooltips.duplicate",
      );
    default:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.categoryTooltips.other",
      );
  }
};

export const getSubCategoryLabel = (
  t: TFunction,
  subCategory: KeywordFeedbackSubCategory,
): string => {
  switch (subCategory) {
    case KeywordFeedbackSubCategory.INAPPROPRIATE:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.subReasons.inappropriate",
      );
    case KeywordFeedbackSubCategory.UNRELATED_REFERENCE:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.subReasons.unrelatedAuthorBookTv",
      );
    case KeywordFeedbackSubCategory.IRRELEVANT:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.subReasons.irrelevant",
      );
    case KeywordFeedbackSubCategory.TOO_SEASONAL:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.subReasons.tooSeasonal",
      );
    case KeywordFeedbackSubCategory.TOO_GENERAL:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.subReasons.tooGeneral",
      );
    case KeywordFeedbackSubCategory.REPEAT:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.subReasons.repeat",
      );
    case KeywordFeedbackSubCategory.METADATA_REFERENCE:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.subReasons.referencesBookMetadata",
      );
    default:
      return t(
        "components:keywordsTab.keywordFeedbackPopover.categories.other",
      );
  }
};

export const getIconTooltip = (t: TFunction, state: IconState): string => {
  switch (state) {
    case "flag":
      return t("components:keywordsTab.keywordFeedbackPopover.flagTooltip");
    case "view":
      return t("components:keywordsTab.keywordFeedbackPopover.viewTooltip");
    default:
      return t("components:keywordsTab.keywordFeedbackPopover.unflagTooltip");
  }
};

export const getIconAria = (t: TFunction, state: IconState): string => {
  switch (state) {
    case "flag":
      return t("components:keywordsTab.keywordFeedbackPopover.flagAria");
    case "view":
      return t("components:keywordsTab.keywordFeedbackPopover.viewAria");
    default:
      return t("components:keywordsTab.keywordFeedbackPopover.unflagAria");
  }
};

interface HeaderInput {
  t: TFunction;
  isSummary: boolean;
  selectedCategory: KeywordFeedbackCategory | null;
  isFreeTextStage: boolean;
}

export interface PopoverHeader {
  title: string;
  subtitle: string | null;
}

export const getPopoverHeader = ({
  t,
  isSummary,
  selectedCategory,
  isFreeTextStage,
}: HeaderInput): PopoverHeader => {
  if (isSummary) {
    return {
      title: t("components:keywordsTab.keywordFeedbackPopover.summaryTitle"),
      subtitle: null,
    };
  }
  if (!selectedCategory) {
    return {
      title: t("components:keywordsTab.keywordFeedbackPopover.popoverTitle"),
      subtitle: t(
        "components:keywordsTab.keywordFeedbackPopover.selectCategory",
      ),
    };
  }
  return {
    title: getCategoryLabel(t, selectedCategory),
    subtitle: isFreeTextStage
      ? t("components:keywordsTab.keywordFeedbackPopover.otherPrompt")
      : t("components:keywordsTab.keywordFeedbackPopover.specifyPrompt"),
  };
};
